import React, { useState, useMemo, useCallback, useEffect } from "react";
import { getColumns } from "./columns";
import {
  showToast,
  showAlert,
  formatDateWithDayjs,
  localDateString,
  getUserCountryCode,
  formatToUSD,
  formatFTNumber,
  formatCurrencyAmount,
  getRepaymentExtensionDays
} from "../../../../utils/utils";
import { FaFilePdf } from "react-icons/fa";
import { Tooltip } from "react-tooltip";
import {
  useTable,
  useRowSelect,
  useFilters,
  useSortBy,
  usePagination,
  useExpanded
} from "react-table";
import {
  repaymentDateAlert,
  alertActions,
  fundedInvoicesData,
  SearchText,
  noDataPresent,
  login,
  tooltips,
  tooltiptextId,
  overdueRE,
  overduetooltip,
  completedTooltip,
  unrecoverableTooltip,
  paymentInitiatedTooltip,
  externalInvoiceStatusTooltip
} from "../../../../locales/en";
import {
  ASC,
  AU_BANK_ACC_NO,
  COUNTRY_CODE_NZ,
  DESC,
  PROCESSING,
  NEGATIVE_SIGN,
  NZ_BANK_ACC_NO,
  PAGE_INDEX,
  PAGE_SIZE_25,
  REPAY_DATE_BLOCKED_EXTERNAL_INVOICE_STATUSES,
  showToastType,
  STATUS_KEY,
  ZERO
} from "../../../../utils/constants/appConstants";
import {
  COMPLETED,
  CURRENCY_FORMAT,
  CUSTNOTREQUIRE,
  DUEDATE,
  FUNDED,
  FUNDINGNOTICE,
  NOTAPPROVED,
  REPAYMENTDATE
} from "../../../../utils/constants/appConstants";
import DatePicker from "../../../datepicker/FundedDatePicker";
import PropTypes from "prop-types";
import FundedTableHeader from "./FundedTableHeader";
import TableSkeleton from "../../../skeletons/TableSkeleton";
import { useDispatch, useSelector } from "react-redux";
import { getQuotesByUser } from "../../../../redux/invoiceSlice";
import { useVisibleColumnCount } from "../../../../hooks/useVisibleColumnCount";
import "./fundedtable.scss";
import {
  getDownloadFundingNoticeUrl,
  getUpdateRepayDateForCustomerUrl
} from "../../../../utils/constants/apiEndPoints";
import privateAxios from "../../../../utils/axiosPrivate";
import ErrorBoundary from "../../../error-boundary";
import ListViewFallback from "../../../error-boundary/fallback/ListViewFallback";
import ExpandToggle from "../../../common/ExpandToggle";
import useResetExpandedOnPagination from "../../../../hooks/useResetExpandedOnPagination";
import PublicPaginationController from "../../../common/PublicPaginationController";
import { useColumnSort } from "../../../../hooks/useColumnSort";
import SortableHeaderCell from "../../../common/SortableHeaderCell";
import { Checkbox } from "../../../common/Checkbox";
import useGroupByRepayDate from "../../../../hooks/useGroupByRepayDate";

const SELECTABLE_STATUSES = ["Pending", "Approved", "In Review", "Funded"];

// The accounting software can close an invoice out from under a funded quote.
// Once it does, the repayment date is fixed, so the row is neither selectable
// nor editable regardless of the quote's own status.
const getBlockingExternalInvoiceStatus = (quote) => {
  const externalInvoiceStatus = quote?.invoiceDto?.externalInvoiceStatus;
  return REPAY_DATE_BLOCKED_EXTERNAL_INVOICE_STATUSES.includes(
    String(externalInvoiceStatus || "").toUpperCase()
  )
    ? externalInvoiceStatus
    : null;
};

const SelectionHeader = ({ rows }) => {
  const safeRows = rows || [];

  const selectableRows = safeRows.filter(
    (row) =>
      SELECTABLE_STATUSES.includes(row.original?.status) &&
      !getBlockingExternalInvoiceStatus(row.original)
  );

  const allSelected =
    selectableRows.length > 0 && selectableRows.every((row) => row.isSelected);

  const handleChange = (e) => {
    const checked = e.target.checked;

    selectableRows.forEach((row) => {
      row.toggleRowSelected(checked);
    });
  };

  if (!selectableRows.length) return null;

  return (
    <Checkbox
      className="headercheckBox"
      checked={allSelected}
      onChange={handleChange}
    />
  );
};

SelectionHeader.propTypes = {
  getToggleAllRowsSelectedProps: PropTypes.func.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired
};

const SelectionCellWrapper = ({ row }) => {
  const isSelectable =
    SELECTABLE_STATUSES.includes(row.original?.status) &&
    !getBlockingExternalInvoiceStatus(row.original);

  return (
    <Checkbox
      {...row.getToggleRowSelectedProps({ disabled: !isSelectable })}
      className="bodycheckBox"
    />
  );
};

SelectionCellWrapper.propTypes = {
  row: PropTypes.object.isRequired
};

const SelectionCell = ({ row }) => {
  return !SELECTABLE_STATUSES.includes(row.original?.status) ? null : (
    <SelectionCellWrapper row={row} />
  );
};

SelectionCell.propTypes = {
  row: PropTypes.object.isRequired
};

const SelectionHeaderWrapper = ({
  rows = [],
  getToggleAllRowsSelectedProps
}) => {
  return (
    <SelectionHeader
      rows={rows}
      getToggleAllRowsSelectedProps={getToggleAllRowsSelectedProps}
    />
  );
};

SelectionHeaderWrapper.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object),
  getToggleAllRowsSelectedProps: PropTypes.func
};

const formatCurrencyWithSign = (amount) =>
  `${amount < ZERO ? NEGATIVE_SIGN : ""}${CURRENCY_FORMAT}${formatCurrencyAmount(
    Math.abs(amount || ZERO)
  )}`;

const getFundedRowId = (row, relativeIndex) =>
  row?.quoteId || row?.id || `${row?.subtotalDate || "row"}-${relativeIndex}`;

const Fundedtable = (props) => {
  const { invoiceType, quoteSummary, onQuoteSummaryRefresh } = props;
  const [tempSearchQuery, setTempSearchQuery] = useState("");
  const [totalPages, setTotalPages] = useState(PAGE_INDEX);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_25);
  const [searchQuery, setSearchQuery] = useState("");
  const [data, setData] = useState([]);
  // The flag is set per customer, so any quote row carries the same value.
  const repaymentExtensionDays = getRepaymentExtensionDays(
    data.some((row) => row?.isExtendedRepaymentDateValidation)
  );
  const columns = useMemo(
    () => getColumns(repaymentExtensionDays),
    [repaymentExtensionDays]
  );
  const dispatch = useDispatch();
  const { fundedLoader } = useSelector((state) => state.invoice);
  const { headerRef, visibleColumnCount } = useVisibleColumnCount();
  const { profile: loggedUserData } = useSelector(
    (state) => state.userManagement
  );

  const userPrimaryCountry = loggedUserData?.userAccountMeta?.primaryCountry;

  const countryCode = getUserCountryCode();

  const userCountryCode = countryCode ? countryCode : userPrimaryCountry;

  const groupedData = useGroupByRepayDate(data);

  const initialState = {
    fmfbFundQuotesId: null,
    status: null,
    fundedAt: null,
    dueDate: null,
    fundingAmount: null,
    repaymentDate: null
  };
  const defaultSort = useMemo(
    () =>
      invoiceType === PROCESSING
        ? { sortBy: REPAYMENTDATE, order: ASC }
        : { sortBy: STATUS_KEY, order: ASC },
    [invoiceType]
  );

  const { columnOrders, handleSort } = useColumnSort(initialState, {
    defaultFirstOrder: DESC
  });
  const sortableColumns = Object.keys(initialState);
  const sortParams = useMemo(() => {
    const activeSort = Object.entries(columnOrders).find(([, value]) => value);

    return {
      sortBy: activeSort?.[0] || defaultSort.sortBy,
      order: activeSort?.[1]?.toLowerCase() || defaultSort.order
    };
  }, [columnOrders, defaultSort]);

  const handleFundedDataGet = useCallback(
    async (pageNumber, pageSize) => {
      try {
        const payload = {
          keyword: searchQuery || undefined,
          page: pageNumber,
          size: pageSize,
          order: sortParams.order,
          sortBy: sortParams.sortBy,
          quoteStatus: invoiceType
        };
        const response = await dispatch(getQuotesByUser({ payload })).unwrap();
        const responseData = response?.data?.data || [];
        setData(responseData);
        setTotalPages(response?.data?.pageInfo?.totalPages);
      } catch (error) {
        setData([]);
        showToast(
          error?.response?.data?.message || showToastType?.errorText,
          showToastType.error
        );
      }
    },
    [searchQuery, dispatch, invoiceType, sortParams]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    page,
    gotoPage,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    pageCount,
    pageOptions,
    selectedFlatRows,
    toggleAllRowsSelected
  } = useTable(
    {
      columns,
      data: groupedData,
      manualPagination: true,
      pageCount: totalPages,
      autoResetPage: false,
      autoResetSelectedRows: false,
      getRowId: getFundedRowId,
      initialState: { pageIndex: PAGE_INDEX, pageSize: PAGE_SIZE_25 }
    },
    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        {
          id: "selection",
          Cell: SelectionCell,
          Header: SelectionHeaderWrapper,
          disableSortBy: true
        },
        ...columns
      ]);
    }
  );

  const { pageIndex } = state;

  useEffect(() => {
    handleFundedDataGet(pageIndex, pageSize);
  }, [
    handleFundedDataGet,
    pageIndex,
    pageSize,
    invoiceType,
    JSON.stringify(columnOrders)
  ]);

  // Reayment Date Change Function Start
  const [showNewDateButton, setShowNewDateButton] = useState(false);
  const [showUpdateButton, setShowUpdateButton] = useState(false);
  const [selectedQuoteId, setSelectedQuoteId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  const handleRepayDate = async (date, rowId) => {
    const formattedDate = date ? localDateString(date) : "";
    setSelectedQuoteId(rowId || null);
    setSelectedDate(formattedDate);
    if (rowId) {
      setShowNewDateButton((prevState) => ({ ...prevState, [rowId]: true }));
    }
    setShowUpdateButton(true);
  };

  const handleNewDateButtonClick = async () => {
    const quoteIds = (
      selectedIds.length > 1 ? selectedIds : [selectedQuoteId]
    ).filter(Boolean);

    if (!quoteIds.length || !selectedDate) return;

    const result = await showAlert(
      repaymentDateAlert?.title,
      repaymentDateAlert?.subtitle,
      alertActions?.warning,
      alertActions?.Confirm,
      alertActions?.Cancel
    );

    if (!result.isConfirmed) return;

    try {
      const dataToSend = {
        quoteIds,
        repaymentDate: selectedDate
      };

      const response = await privateAxios.put(
        getUpdateRepayDateForCustomerUrl,
        dataToSend
      );

      if (response.status === 200) {
        showToast(response.data.message, showToastType?.success);
        setShowNewDateButton(false);
        setShowUpdateButton(false);
        setSelectedQuoteId(null);
        setSelectedDate(null);
        toggleAllRowsSelected(false);
        handleFundedDataGet(pageIndex, pageSize);
        // Moving a repayment date changes how much of the account is due, so
        // the totals below the table have to be recomputed alongside the rows.
        onQuoteSummaryRefresh?.();
      }
    } catch (error) {
      showToast(
        error?.response?.data?.message || showToastType?.errorText,
        showToastType?.error
      );
    }
  };

  const [expandedRows, setExpandedRows] = useState([]);

  const isRowExpanded = (rowId) => expandedRows.includes(rowId);

  const handleDownload = (pdfUrl) => {
    const anchor = document.createElement("a");
    anchor.href = pdfUrl;
    anchor.download = pdfUrl.split("/").pop();
    anchor.click();
  };

  // A row carries a link only when a funding notice has already been generated for
  // it, which is what the list signs. Quotes with no notice on file fall back to
  // the download endpoint: it builds the PDF on request and records where it put
  // it, so this row links straight to it from the next load onwards.
  const handleFundingNoticeDownload = async (quote) => {
    if (quote.fundingNotice) {
      handleDownload(quote.fundingNotice);
      return;
    }
    try {
      const response = await privateAxios.get(
        getDownloadFundingNoticeUrl(quote.quoteId),
        { responseType: "blob" }
      );
      const objectUrl = window.URL.createObjectURL(new Blob([response.data]));
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = `funding_notice_${quote.quoteId}.pdf`;
      anchor.click();
      window.URL.revokeObjectURL(objectUrl);
    } catch {
      // The response body is a Blob on this request, so the server's message is
      // not readable here.
      showToast(showToastType?.errorText, showToastType.error);
    }
  };

  const toggleRow = (rowId) => {
    setExpandedRows((prevState) =>
      prevState.includes(rowId)
        ? prevState.filter((id) => id !== rowId)
        : [...prevState, rowId]
    );
  };

  // Handle changes in the search input
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setTempSearchQuery(value);
    if (value.trim() === "") {
      setSearchQuery("");
    }
  };

  // Handle key press events in the search input
  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      const trimmedQuery = tempSearchQuery.trim();
      setSearchQuery(trimmedQuery);
      gotoPage(0);
    }
  };
  useResetExpandedOnPagination(
    () => setExpandedRows([]),
    [pageIndex, pageSize, searchQuery, invoiceType]
  );

  const paginationParams = {
    gotoPage,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    pageIndex,
    pageCount,
    pageOptions,
    setPageSize,
    pageSize,
    dataLength: data.length
  };

  const selectedIds = selectedFlatRows.map((row) => row.original.quoteId);

  const earliestDueDateSelected = useMemo(() => {
    const timestamps = selectedFlatRows
      .map((row) => row.original)
      .filter((r) => !r.isSubtotal)
      .map((r) => {
        if (r.quoteId === selectedQuoteId && selectedDate) {
          return new Date(selectedDate).getTime();
        }

        return new Date(r.repaymentDate || r.dueDate).getTime();
      })
      .filter((t) => !isNaN(t));

    return timestamps.length ? new Date(Math.min(...timestamps)) : null;
  }, [selectedFlatRows, selectedDate, selectedQuoteId]);

  // The flag is computed per customer, so every selected row (aside from
  // subtotal rows, which don't carry it) shares the same value.
  const isExtendedRepaymentDateValidationSelected = useMemo(
    () =>
      selectedFlatRows.find((row) => !row.original?.isSubtotal)?.original
        ?.isExtendedRepaymentDateValidation ?? false,
    [selectedFlatRows]
  );

  const getSelectedDateForRow = (row) => {
    if (selectedQuoteId === row.original.quoteId && selectedDate) {
      return new Date(selectedDate);
    }

    if (row.original.repaymentDate) {
      const d = new Date(row.original.repaymentDate);
      return isNaN(d) ? null : d;
    }

    if (row.original.dueDate) {
      const d = new Date(row.original.dueDate);
      return isNaN(d) ? null : d;
    }

    return null;
  };

  useEffect(() => {
    setSelectedDate(null);
    setSelectedQuoteId(null);
    setShowUpdateButton(false);
  }, [selectedFlatRows]);

  return (
    <div className="funded-data-filter-tab mt-3">
      <div className="container p-0">
        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center">
              <div className="pagination-dropdown-wrapper">
                {data.length > 0 && (
                  <PublicPaginationController
                    {...paginationParams}
                    showButtons={false}
                  />
                )}
              </div>
              <FundedTableHeader
                tempSearchQuery={tempSearchQuery}
                handleSearchChange={handleSearchChange}
                handleKeyDown={handleKeyDown}
                SearchText={SearchText}
                selectedIds={selectedIds}
                selectedDate={selectedDate}
                earliestDueDateSelected={earliestDueDateSelected}
                isExtendedRepaymentDateValidation={
                  isExtendedRepaymentDateValidationSelected
                }
                onDateChange={handleRepayDate}
                onNewDateChange={handleNewDateButtonClick}
                showUpdateButton={showUpdateButton}
              />
            </div>
            <ErrorBoundary fallback={<ListViewFallback />}>
              <div className="fundedtable-wrap">
                <table
                  className="mb-3 fundedtable no-table-header-bg"
                  {...getTableProps()}
                >
                  <thead ref={headerRef}>
                    {headerGroups.map((headerGroup) => {
                      const { key, ...rest } =
                        headerGroup.getHeaderGroupProps();
                      return (
                        <tr className="parentrow" {...rest} key={key}>
                          <th />

                          {headerGroup.headers.map((column) => {
                            const isSortable = sortableColumns.includes(
                              column.id
                            );

                            return (
                              <SortableHeaderCell
                                key={column.id}
                                column={column}
                                isSortable={isSortable}
                                columnOrders={columnOrders}
                                handleSort={handleSort}
                                tableSortWithIcon
                              />
                            );
                          })}
                        </tr>
                      );
                    })}
                  </thead>
                  <tbody {...getTableBodyProps()}>
                    {(() => {
                      if (fundedLoader) {
                        return (
                          <TableSkeleton
                            rows={6}
                            columns={visibleColumnCount}
                          />
                        );
                      }
                      if (page.length === 0) {
                        return (
                          <tr>
                            <td
                              colSpan={columns.length + 2}
                              className="text-center"
                            >
                              {noDataPresent}
                            </td>
                          </tr>
                        );
                      }
                      return page.map((row) => {
                        prepareRow(row);
                        const isExpanded = isRowExpanded(row.id);
                        const isOverdue =
                          row.original.status === overdueRE.rowItems.item17;
                        const isCompleted =
                          row.original.status === overdueRE.rowItems.item1;
                        const isUnrecoverable =
                          row.original.status === overdueRE.rowItems.item8;
                        const isPaymentInitiated =
                          Number(row.original.totalPaymentAmount) > 0;
                        const blockingExternalInvoiceStatus =
                          getBlockingExternalInvoiceStatus(row.original);
                        const isDisabled =
                          isPaymentInitiated ||
                          Boolean(blockingExternalInvoiceStatus) ||
                          isOverdue ||
                          isCompleted ||
                          isUnrecoverable ||
                          selectedIds.length > 1;
                        let rowTooltip;
                        if (isPaymentInitiated) {
                          rowTooltip = paymentInitiatedTooltip;
                        } else if (blockingExternalInvoiceStatus) {
                          rowTooltip = {
                            id: externalInvoiceStatusTooltip.id,
                            text: externalInvoiceStatusTooltip.text.replace(
                              "{{status}}",
                              blockingExternalInvoiceStatus
                            )
                          };
                        } else if (isCompleted) {
                          rowTooltip = completedTooltip;
                        } else if (isUnrecoverable) {
                          rowTooltip = unrecoverableTooltip;
                        } else if (isOverdue) {
                          rowTooltip = overduetooltip;
                        }
                        const tooltipId = rowTooltip?.id;
                        const tooltipContent = rowTooltip?.text;

                        const originalDueDateValue = row.original?.invoiceDto
                          ?.originalDueDate
                          ? new Date(row.original?.invoiceDto?.originalDueDate)
                          : null;

                        if (row.original?.isSubtotal) {
                          return (
                            <tr
                              key={`subtotal-${row.original?.subtotalDate}`}
                              className="subtotal-row"
                            >
                              {columns
                                .slice(0, columns.length - 4)
                                .map((_, index) => (
                                  <td key={`blank-${index}`} />
                                ))}

                              <td className="text-end" colSpan={3}>
                                {`${fundedInvoicesData?.funded?.subTotal} `}
                                {formatDateWithDayjs(
                                  row.original?.subtotalDate,
                                  "MMM DD, YYYY"
                                )}
                                :
                              </td>

                              <td>
                                {formatCurrencyWithSign(
                                  row.original?.subtotalAmount
                                )}
                              </td>
                              <td />
                            </tr>
                          );
                        }

                        const renderCell = (cell) => {
                          if (cell.column.id === FUNDINGNOTICE) {
                            const hasNotice =
                              Boolean(row.original?.fundingNotice) ||
                              [FUNDED, COMPLETED].includes(
                                row.original?.status
                              );
                            if (!hasNotice) {
                              return null;
                            }
                            return (
                              <button
                                className="notice-button"
                                onClick={() =>
                                  handleFundingNoticeDownload(row.original)
                                }
                                data-tooltip-id={tooltiptextId.fundingPdfId}
                                data-tooltip-content={tooltips.downloadPdf}
                              >
                                <FaFilePdf />
                              </button>
                            );
                          }
                          if (cell.column.id === REPAYMENTDATE) {
                            if (
                              [NOTAPPROVED, CUSTNOTREQUIRE].includes(
                                row.original.status
                              )
                            ) {
                              return null;
                            }
                            return (
                              <>
                                <DatePicker
                                  key={
                                    row.original.repaymentDate
                                      ? row.original.repaymentDate
                                      : row.original.dueDate
                                  }
                                  selected={getSelectedDateForRow(row)}
                                  originalDueDate={originalDueDateValue}
                                  isExtendedRepaymentDateValidation={
                                    row.original
                                      ?.isExtendedRepaymentDateValidation
                                  }
                                  onChange={(date) =>
                                    handleRepayDate(date, row.original.quoteId)
                                  }
                                  disabled={isDisabled}
                                  tooltipId={tooltipId}
                                  tooltipContent={tooltipContent}
                                />
                                {showNewDateButton[row.original.quoteId] && (
                                  <button
                                    className="btn btn-primary new-pay-date"
                                    onClick={handleNewDateButtonClick}
                                  >
                                    {fundedInvoicesData?.funded?.newDate}
                                  </button>
                                )}
                              </>
                            );
                          }
                          if (cell.column.id === DUEDATE) {
                            return row.original.dueDate
                              ? formatDateWithDayjs(
                                  row.original.dueDate,
                                  "DD-MMM-YYYY"
                                )
                              : formatDateWithDayjs(
                                  row.original.repaymentDate,
                                  "DD-MMM-YYYY"
                                );
                          }
                          return cell.render("Cell");
                        };

                        return (
                          <React.Fragment key={row.id}>
                            <tr className="parentrow" {...row.getRowProps()}>
                              <td className="expand-td">
                                <ExpandToggle
                                  isExpanded={isExpanded}
                                  onToggle={() => toggleRow(row.id)}
                                  id={tooltiptextId.fundingPdfId}
                                />
                              </td>
                              {row.cells.map((cell) => (
                                <td
                                  key={cell.getCellProps().key}
                                  {...cell.getCellProps()}
                                >
                                  {renderCell(cell)}
                                </td>
                              ))}
                            </tr>
                            {isExpanded && (
                              <tr className="colexpand childrow">
                                <td colSpan={columns.length + 2}>
                                  <div className="tdbody">
                                    {columns.map((column) => {
                                      const value =
                                        row.original[column.accessor];
                                      const isCurrencyField = [
                                        "fundingAmount",
                                        "feeUntilDueDate",
                                        "totalPaymentAmount",
                                        "totalDueAmount"
                                      ].includes(column.accessor);

                                      const isDateField = [
                                        "fundedAt",
                                        "dueDate"
                                      ].includes(column.accessor);

                                      let displayValue;
                                      if (
                                        column.accessor === "fmfbFundQuotesId"
                                      ) {
                                        displayValue = formatFTNumber(value);
                                      } else if (isDateField) {
                                        displayValue = formatDateWithDayjs(
                                          value,
                                          "DD-MMM-YYYY"
                                        );
                                      } else if (isCurrencyField) {
                                        displayValue = formatToUSD(value);
                                      } else {
                                        displayValue = String(value);
                                      }

                                      return (
                                        <span
                                          key={column.accessor}
                                          data-label={column.Header}
                                        >
                                          {displayValue}
                                        </span>
                                      );
                                    })}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
              <Tooltip id={tooltiptextId.fundingPdfId} place="top" />
            </ErrorBoundary>
            {data.length > 0 && (
              <PublicPaginationController {...paginationParams} showButtons />
            )}
            <div className="row invoice_calc mt-5">
              <div className="col-md-8">
                <div className="d-flex mb-3 justify-content-between">
                  <div className="invcs_calc_heading">
                    {fundedInvoicesData?.funded?.dueNow}
                  </div>
                  <div className="invcs_calc_heading">
                    {formatCurrencyWithSign(quoteSummary?.dueNow)}
                  </div>
                </div>
                <div className="d-flex mb-3 justify-content-between">
                  <div className="invcs_calc_heading">
                    {fundedInvoicesData?.funded?.totalOwing}
                  </div>
                  <div className="invcs_calc_heading">
                    {formatCurrencyWithSign(quoteSummary?.totalOwing)}
                  </div>
                </div>
                <div className="d-flex mb-3 flex-column">
                  <small>{fundedInvoicesData?.funded?.text1}</small>
                  <p>{fundedInvoicesData?.funded?.text2}</p>
                  <p>
                    <strong>
                      {login?.mainTitle2} <br />
                      {userCountryCode === COUNTRY_CODE_NZ
                        ? NZ_BANK_ACC_NO
                        : AU_BANK_ACC_NO}
                    </strong>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Tooltip id="overdue-tooltip" />
      <Tooltip id="completed-tooltip" />
      <Tooltip id="unrecoverable-tooltip" />
      <Tooltip id="payment-initiated-tooltip" />
      <Tooltip id={externalInvoiceStatusTooltip.id} />
    </div>
  );
};

Fundedtable.propTypes = {
  invoiceType: PropTypes.string.isRequired,
  quoteSummary: PropTypes.shape({
    dueNow: PropTypes.number,
    totalOwing: PropTypes.number
  }),
  onQuoteSummaryRefresh: PropTypes.func
};

export default Fundedtable;
